import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog, clearActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";
import { formatPayloadValue } from "../../../helpers/activityLogHelpers";
import ActivityLogTimelineView from "../../../components/ActivityLog/ActivityLogTimelineView";

const INVOICE_FINANCING_CATEGORY_ID = "DCT1000001";

const DealActivityLog = ({
  dealId,
  orgId,
  dealName,
  dealCategoryId = INVOICE_FINANCING_CATEGORY_ID,
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs, loading, totalRecords } = useSelector((state) => state.activityLog);

  useEffect(() => {
    setPage(1);
  }, [dealId, orgId]);

  useEffect(() => {
    if (orgId && dealId) {
      dispatch(
        getActivityLog({
          module: "BUSINESS",
          entityId: orgId,
          dealId,
          dealCategoryId: dealCategoryId || INVOICE_FINANCING_CATEGORY_ID,
          page,
          limit,
        })
      );
    }
  }, [dispatch, orgId, dealId, page, dealCategoryId]);

  useEffect(() => {
    return () => {
      dispatch(clearActivityLog());
    };
  }, [dispatch]);

  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];

    return Object.entries(payload).filter(([key]) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (
        lowerKey === "dealcategoryid" ||
        lowerKey === "dealcategoryname" ||
        lowerKey === "dealid"
      ) {
        return false;
      }
      return true;
    });
  };

  const formatPayloadKey = (key) => {
    if (key === "approvedAmt") return "Approved Amount";
    if (key === "invoiceAmount" || key === "totalInvoiceValue") {
      return isSupplyChainCategory(dealCategoryId) ? "Purchase Order Amount" : "Invoice Amount";
    }
    if (key === "fundRequired") return "Fund Required";
    if (key === "fundedAmt") return "Funded Amount";
    if (key === "disbursedAmt") return "Disbursed Amount";
    if (key === "settledAmt") return "Settled Amount";
    if (key === "balanceAmt") return "Balance Amount";
    if (key === "stampFee") return "Stamp Fee";
    if (key === "fullySettled") return "Fully Settled";
    if (key === "investorOrgId") return "Investor Org Id";
    if (key === "isDuplicate") return "Duplicate";
    if (key === "manual") return "Manual Disbursement";
    return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
  };

  const getActionConfig = (action) => {
    switch (action) {
      case "DEAL_UPLOADED":
        return { label: "Deal Uploaded", color: "#e6f7ff", textColor: "#1890ff" };
      case "DEAL_APPROVED":
        return { label: "Deal Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "DEAL_REJECTED":
        return { label: "Deal Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "DEAL_FUNDED":
        return { label: "Deal Funded", color: "#fff7e6", textColor: "#fa8c16" };
      case "DEAL_PENDING_DISBURSEMENT":
        return { label: "Pending Disbursement", color: "#fffbe6", textColor: "#d4b106" };
      case "DEAL_DISBURSED":
        return { label: "Deal Disbursed", color: "#f9f0ff", textColor: "#722ed1" };
      case "DEAL_CANCELLED":
        return { label: "Deal Cancelled", color: "#fff0f6", textColor: "#eb2f96" };
      case "DEAL_REPUBLISHED":
        return { label: "Deal Republished", color: "#e6f7ff", textColor: "#1890ff" };
      case "DEAL_PARTIALLY_SETTLED":
        return { label: "Partially Settled", color: "#fff7e6", textColor: "#fa8c16" };
      case "DEAL_SETTLED":
        return { label: "Deal Settled", color: "#f6ffed", textColor: "#389e0d" };
      default:
        return {
          label: String(action || "").replace(/^DEAL_/, "").replace(/_/g, " "),
          color: "#e6f7ff",
          textColor: "#1890ff",
        };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const target =
      dealName || log.payload?.dealName || dealId || "this deal";

    if (log.action === "DEAL_UPLOADED") {
      return `<strong>${name}</strong> (${role}) <strong>UPLOADED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_FUNDED") {
      return `<strong>${name}</strong> (${role}) <strong>FUNDED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_PENDING_DISBURSEMENT") {
      return `<strong>${name}</strong> (${role}) <strong>MARKED PENDING DISBURSEMENT</strong> for deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_DISBURSED") {
      return `<strong>${name}</strong> (${role}) <strong>DISBURSED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_CANCELLED") {
      return `<strong>${name}</strong> (${role}) <strong>CANCELLED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_REPUBLISHED") {
      return `<strong>${name}</strong> (${role}) <strong>REPUBLISHED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_PARTIALLY_SETTLED") {
      return `<strong>${name}</strong> (${role}) <strong>PARTIALLY SETTLED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_SETTLED") {
      return `<strong>${name}</strong> (${role}) <strong>SETTLED</strong> deal <strong>"${target}"</strong>`;
    }

    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on deal <strong>"${target}"</strong>`;
  };

  if (!orgId || !dealId) {
    return (
      <div style={{ textAlign: "center", color: "#8c8c8c", padding: "40px 0" }}>
        Unable to load activity log for this deal.
      </div>
    );
  }

  return (
    <ActivityLogTimelineView
      title="Deal Lifecycle History"
      totalRecords={totalRecords}
      loading={loading}
      logs={logs}
      page={page}
      setPage={setPage}
      limit={limit}
      getActionConfig={getActionConfig}
      formatDesc={formatDesc}
      filterPayloadEntries={filterPayloadEntries}
      formatPayloadKey={formatPayloadKey}
      formatPayloadValue={formatPayloadValue}
    />
  );
};

export default DealActivityLog;
